import { getQueryTimeInterval } from 'core/util/dateUtils';
import {
  applicationRenderer,
  asnRenderer,
  asPathRenderer,
  bgpCommunityRenderer,
  cdnRenderer,
  cityRenderer,
  cloudDetailRenderer,
  connectivityTypeRenderer,
  countryRenderer,
  deviceNameRenderer,
  hideRawValueRenderer,
  inetFamilyRenderer,
  interfaceRenderer,
  ipRenderer,
  kappaRenderer,
  networkBoundaryRenderer,
  ottProviderRenderer,
  ottServiceRenderer,
  ottServiceTypeRenderer,
  packetSizeRenderer,
  portRenderer,
  protocolRenderer,
  providerRenderer,
  regionRenderer,
  routePrefixRenderer,
  serviceRenderer,
  siteMarketRenderer,
  siteNameRenderer,
  valueOrNameRenderer
} from 'app/components/dataviews/views/legend/legendRenderers';
import {
  applicationMetrics,
  asnMetrics,
  asPathMetrics,
  cdnMetrics,
  cityMetrics,
  countryMetrics,
  interfaceMetrics,
  ipMetrics,
  ottProviderMetrics,
  ottServiceMetrics,
  ottServiceTypeMetrics,
  regionMetrics,
  serviceMetrics,
  siteMarketMetrics,
  siteMetrics
} from 'app/components/dataviews/views/legend/legendUtils';
import { all as cloudDetailMetrics } from 'app/views/core/cloud/cloudDetailMetrics';
import $dictionary from 'app/stores/$dictionary';

/**
 * @param {string} metric
 * @param {import('./types.d.ts').QueryModel} firstQuery
 * @param {number} index
 * @param {string} fastData
 * @returns {import('./types.d.ts').RenderFn}
 */
export function getMetricRenderer({ metric, firstQuery, index, fastData }) {
  if (fastData !== 'Full' && (metric === 'Port_src' || metric === 'Port_dst')) {
    const lookback_seconds = firstQuery.get('lookback_seconds');
    const starting_time = firstQuery.get('starting_time');
    const ending_time = firstQuery.get('ending_time');
    const queryInterval = getQueryTimeInterval({ lookback_seconds, starting_time, ending_time });
    return portRenderer(index === 0, false, fastData, queryInterval);
  }
  if (countryMetrics.includes(metric)) {
    return countryRenderer();
  }
  if (cityMetrics.includes(metric)) {
    return cityRenderer();
  }
  if (regionMetrics.includes(metric)) {
    return regionRenderer();
  }
  if (interfaceMetrics.includes(metric)) {
    return interfaceRenderer();
  }
  if (metric === 'i_device_id' || metric === 'i_ult_exit_device_name') {
    return deviceNameRenderer();
  }
  if (siteMetrics.includes(metric)) {
    return siteNameRenderer();
  }
  if (siteMarketMetrics.includes(metric)) {
    return siteMarketRenderer();
  }
  if (metric === 'ktsubtype__kappa__STR08' || metric === 'ktsubtype__kappa__STR14') {
    return kappaRenderer();
  }
  if (metric === 'inet_family') {
    return inetFamilyRenderer();
  }
  if (metric === 'sampledpktsize') {
    return packetSizeRenderer();
  }
  if (metric === 'Proto' || metric === 'protocol') {
    return protocolRenderer();
  }
  if (metric === 'src_route_prefix_len' || metric === 'dst_route_prefix_len') {
    return routePrefixRenderer();
  }
  if (
    metric === 'i_src_network_bndry_name' ||
    metric === 'i_dst_network_bndry_name' ||
    metric === 'i_ult_exit_network_bndry_name'
  ) {
    return networkBoundaryRenderer();
  }
  if (
    metric === 'src_bgp_community' ||
    metric === 'dst_bgp_community' ||
    metric === 'src_bgp_ext_community' ||
    metric === 'dst_bgp_ext_community' ||
    metric === 'src_bgp_large_community' ||
    metric === 'dst_bgp_large_community'
  ) {
    return bgpCommunityRenderer();
  }
  if (
    metric === 'i_src_connect_type_name' ||
    metric === 'i_dst_connect_type_name' ||
    metric === 'i_ult_exit_connect_type_name'
  ) {
    return connectivityTypeRenderer();
  }
  if (
    metric === 'i_src_provider_classification' ||
    metric === 'i_dst_provider_classification' ||
    metric === 'i_ult_provider_classification'
  ) {
    return providerRenderer();
  }
  if (asnMetrics.includes(metric)) {
    return asnRenderer();
  }
  if (asPathMetrics.includes(metric)) {
    return asPathRenderer();
  }
  if (serviceMetrics.includes(metric)) {
    return serviceRenderer();
  }
  if (ipMetrics.includes(metric)) {
    return ipRenderer();
  }
  if (cdnMetrics.includes(metric)) {
    return cdnRenderer();
  }
  if (ottProviderMetrics.includes(metric)) {
    return ottProviderRenderer();
  }
  if (ottServiceMetrics.includes(metric)) {
    return ottServiceRenderer();
  }
  if (ottServiceTypeMetrics.includes(metric)) {
    return ottServiceTypeRenderer();
  }
  if (applicationMetrics.includes(metric)) {
    return applicationRenderer();
  }
  if (cloudDetailMetrics.includes(metric)) {
    return cloudDetailRenderer();
  }
  if ($dictionary.get('hideRawValueDimensions', []).includes(metric)) {
    return hideRawValueRenderer();
  }
  if (index === 0) {
    return valueOrNameRenderer();
  }
  return null;
}

export const mergeColumns = (columns = [], overrides = []) => {
  const mergedColumns = [...columns];

  overrides.forEach((override) => {
    const idx = mergedColumns.findIndex(
      (col) => (col.key && col.key === override.key) || (col.name && col.name === override.name)
    );

    if (idx > -1) {
      mergedColumns[idx] = {
        ...mergedColumns[idx],
        ...override
      };
    } else {
      mergedColumns.push(override);
    }
  });
  return mergedColumns;
};
